import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { s3UploadService } from '../../services/s3UploadService';
import { bucketConfigService } from '../../services/bucketConfigService';
import { uploadHistoryService } from '../../services/uploadHistoryService';
import { userStorageFolderService } from '../../services/userStorageFolderService';
import NavigationBar from '../../components/ui/NavigationBar';
import UploadModal from '../../components/ui/UploadModal';
import FilePreviewModal from '../../components/ui/FilePreviewModal';
import BucketSelector from '../../components/ui/BucketSelector';
import FileCard from './components/FileCard';
import BreadcrumbNav from './components/BreadcrumbNav';
import SearchBar from './components/SearchBar';
import StorageQuota from './components/StorageQuota';
import BulkActions from './components/BulkActions';
import ContextMenu from './components/ContextMenu';
import DeleteConfirmDialog from './components/DeleteConfirmDialog';
import EmptyState from './components/EmptyState';
import ErrorStateDisplay from './components/ErrorStateDisplay';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import CORSTroubleshootingGuide from './components/CORSTroubleshootingGuide';
import { ToastContainer } from '../../components/ui/Toast';
import useToast from '../../hooks/useToast';

const FileBrowser = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeBucket, setActiveBucket] = useState(null);
  const [bucketConfigs, setBucketConfigs] = useState([]);
  const [currentPath, setCurrentPath] = useState('/');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [contextMenu, setContextMenu] = useState({ isOpen: false, position: { x: 0, y: 0 }, file: null });
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, files: [] });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCORSGuide, setShowCORSGuide] = useState(false);
  const { toasts, showToast, removeToast } = useToast();
  const [isActuallyUploading, setIsActuallyUploading] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [currentBucket, setCurrentBucket] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentFolder, setCurrentFolder] = useState('');
  const [activeFolder, setActiveFolder] = useState(null); // Track active folder from storage
  const [isFolderLoading, setIsFolderLoading] = useState(true);
  const [folderSyncStatus, setFolderSyncStatus] = useState(null);
  const [errorType, setErrorType] = useState('generic');
  const [hasInitialLoadCompleted, setHasInitialLoadCompleted] = useState(false); // NEW: Track first load

  // ENHANCED: Detect specific error types from S3 service responses
  const detectErrorType = (err) => {
    if (!err) return 'generic';
    
    let errorMessage = err?.message || '';
    const errorName = err?.name || '';
    
    // Empty results
    if (errorMessage?.includes('No files found') || errorMessage?.includes('empty')) {
      return 'empty_results';
    }
    
    // CORS errors
    if (errorMessage === 'CORS_ERROR' || errorMessage?.includes('CORS') || 
        errorMessage?.includes('Failed to fetch') ||
        err?.troubleshooting?.type === 'CORS') {
      return 'cors_error';
    }
    
    // Connection failures
    if (errorMessage?.includes('Failed to connect') || 
        errorMessage?.includes('Network error') ||
        errorMessage?.includes('NetworkingError') ||
        errorName === 'NetworkingError') {
      return 'connection_failed';
    }
    
    // Permission denied
    if (errorMessage?.includes('Access denied') || 
        errorMessage?.includes('AccessDenied') ||
        errorMessage?.includes('Forbidden') ||
        errorName === 'AccessDenied' ||
        errorName === 'Forbidden') {
      return 'permission_denied';
    }
    
    // Bucket not found
    if (errorMessage?.includes('NoSuchBucket') ||
        errorMessage?.includes('does not exist') ||
        errorName === 'NoSuchBucket') {
      return 'bucket_not_found';
    }
    
    // File loading error
    if (errorMessage?.includes('Failed to load')) {
      return 'file_load_error';
    }
    
    return 'generic';
  };

  // CRITICAL FIX: Simplified loadFiles that ALWAYS clears loading state
  const loadFiles = async () => {
    if (!currentBucket) {
      console.log('⏸️ No currentBucket available - skipping file load');
      setIsLoading(false);
      setLoading(false);
      return;
    }

    try {
      console.log('🔄 Starting loadFiles()...', {
        currentBucket,
        activeFolder: activeFolder?.folder_path,
        hasInitialLoadCompleted
      });

      setIsLoading(true);
      setLoading(true); // CRITICAL: Set BOTH loading states
      setError(null);
      setErrorType('generic');

      // Get current bucket configuration
      const configs = await bucketConfigService?.getAll();
      const bucketConfig = configs?.find(c => c?.id === currentBucket);

      if (!bucketConfig) {
        throw new Error('Bucket configuration not found');
      }

      // Initialize S3 client with bucket config
      await s3UploadService?.initializeClient(bucketConfig);

      // Build list options with active folder prefix if available
      const listOptions = {
        maxKeys: 1000
      };

      if (activeFolder?.folder_path) {
        listOptions.prefix = activeFolder?.folder_path?.endsWith('/') 
          ? activeFolder?.folder_path 
          : `${activeFolder?.folder_path}/`;
      }

      console.log('📂 Calling S3 listFiles with options:', listOptions);

      const result = await s3UploadService?.listFiles(listOptions);

      console.log('📦 S3 listFiles result:', {
        success: result?.success,
        fileCount: result?.files?.length || 0
      });

      if (result?.success) {
        // Empty folder is a valid state
        if (!result?.files || result?.files?.length === 0) {
          console.log('✅ Empty folder - ready for uploads');
          setFiles([]);
          setError(null);
        } else {
          console.log('✅ Files loaded successfully:', result?.files?.length);
          setFiles(result?.files || []);
          setError(null);
        }
      } else {
        throw new Error('Failed to load files from S3');
      }
    } catch (err) {
      console.error('❌ Error in loadFiles():', err);
      
      const detectedErrorType = detectErrorType(err);
      setErrorType(detectedErrorType);
      
      if (
        err?.message === 'CORS_ERROR' || 
        err?.troubleshooting?.type === 'CORS'|| err?.message?.includes('CORS') ||
        err?.message?.includes('Failed to fetch')
      ) {
        setError('CORS configuration required - your S3 bucket needs CORS settings to allow browser access');
        setShowCORSGuide(true);
      } else {
        let errorMessage = err?.message || 'Failed to load files from S3';
        
        switch (detectedErrorType) {
          case 'connection_failed':
            errorMessage = 'Connection failed - check your network and AWS credentials';
            break;
          case 'permission_denied':
            errorMessage = 'Access denied - verify bucket permissions and IAM policy';
            break;
          case 'bucket_not_found':
            errorMessage = `Bucket not found - check bucket name and region`;
            break;
          case 'file_load_error':
            errorMessage = 'Failed to load files - try again or check bucket configuration';
            break;
        }
        
        setError(errorMessage);
      }
    } finally {
      // CRITICAL: ALWAYS clear loading states in finally block
      console.log('✅ loadFiles() completed - clearing loading states');
      setIsLoading(false);
      setLoading(false);
      setHasInitialLoadCompleted(true); // Mark initial load as done
    }
  };

  // Calculate total storage used by files in the bucket
  const calculateTotalStorageUsed = () => {
    if (!files || files?.length === 0) return 0;
    
    return files?.reduce((total, file) => {
      // Only count files, not folders
      if (!file?.isFolder && file?.size) {
        return total + file?.size;
      }
      return total;
    }, 0);
  };

  // Initialize sidebar state based on screen size
  useEffect(() => {
    const handleResize = () => {
      // Open sidebar by default on desktop (lg breakpoint = 1024px)
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
      }
    };

    // Set initial state
    handleResize();

    // Listen for resize events
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (isSidebarOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isSidebarOpen]);

  // Load bucket configurations on mount
  useEffect(() => {
    if (!user) return;

    const loadBucketConfigs = async () => {
      try {
        const configs = await bucketConfigService?.getAll();
        setBucketConfigs(configs);
        
        // Set first bucket as active or default bucket
        const defaultBucket = configs?.find(c => c?.isDefault) || configs?.[0];
        if (defaultBucket) {
          setActiveBucket(defaultBucket?.id);
        }
      } catch (err) {
        console.error('Error loading bucket configs:', err);
        setError(err?.message || 'Failed to load bucket configurations');
      }
    };

    loadBucketConfigs();
  }, [user]);

  // SIMPLIFIED: Load active folder WITHOUT blocking file loading
  useEffect(() => {
    if (!user) return;

    const loadActiveFolder = async () => {
      try {
        setIsFolderLoading(true);
        setFolderSyncStatus({ status: 'loading', message: 'Loading active folder...' });
        
        const activeFolderData = await userStorageFolderService?.getActiveFolder();
        
        if (activeFolderData) {
          console.log('✅ Active folder found:', activeFolderData?.folder_path);

          setActiveFolder(activeFolderData);
          s3UploadService?.setActiveFolder(activeFolderData?.folder_path);
          
          if (activeFolderData?.bucket_configurations?.id) {
            setCurrentBucket(activeFolderData?.bucket_configurations?.id);
            setActiveBucket(activeFolderData?.bucket_configurations?.id);
          }

          // Background sync verification (non-blocking)
          setFolderSyncStatus({ 
            status: 'verified', 
            message: 'Folder loaded',
            folderPath: activeFolderData?.folder_path 
          });

        } else {
          // No active folder - check for existing folders
          const allFolders = await userStorageFolderService?.getAll();
          
          if (allFolders && allFolders?.length > 0) {
            const firstFolder = allFolders?.[0];
            
            await userStorageFolderService?.setActiveFolder(firstFolder?.id);
            const newActiveFolderData = await userStorageFolderService?.getActiveFolder();
            
            if (newActiveFolderData) {
              setActiveFolder(newActiveFolderData);
              s3UploadService?.setActiveFolder(newActiveFolderData?.folder_path);
              
              if (newActiveFolderData?.bucket_configurations?.id) {
                setCurrentBucket(newActiveFolderData?.bucket_configurations?.id);
                setActiveBucket(newActiveFolderData?.bucket_configurations?.id);
              }
              
              setFolderSyncStatus({ 
                status: 'verified', 
                message: 'Folder auto-activated',
                folderPath: newActiveFolderData?.folder_path 
              });
              
              showToast(`📁 Activated folder: ${newActiveFolderData?.folder_path}`, 'success');
            }
          } else {
            // No folders - use bucket root
            setActiveFolder(null);
            s3UploadService?.clearActiveFolder();
            setFolderSyncStatus({ status: 'none', message: 'No folders available' });
            
            if (activeBucket) {
              setCurrentBucket(activeBucket);
            }
          }
        }
      } catch (error) {
        console.error('❌ Error loading active folder:', error);
        setActiveFolder(null);
        s3UploadService?.clearActiveFolder();
        setFolderSyncStatus({ 
          status: 'error', 
          message: 'Failed to load folder',
          error: error?.message 
        });
        
        // Even on error, allow file loading if bucket is available
        if (activeBucket) {
          setCurrentBucket(activeBucket);
        }
      } finally {
        // CRITICAL: Always mark folder loading as complete
        setIsFolderLoading(false);
      }
    };

    loadActiveFolder();
  }, [user, activeBucket]);

  // CRITICAL FIX: Simplified file loading trigger - NO COMPLEX CONDITIONS
  useEffect(() => {
    // Simple rule: Load files once we have currentBucket and folder loading is done
    if (!isFolderLoading && currentBucket && !hasInitialLoadCompleted) {
      console.log('🚀 Triggering initial file load');
      loadFiles();
    }
  }, [isFolderLoading, currentBucket, hasInitialLoadCompleted]);

  // ADDITIONAL: Reload files when activeFolder changes (after initial load)
  useEffect(() => {
    if (hasInitialLoadCompleted && currentBucket) {
      console.log('🔄 Active folder changed - reloading files');
      loadFiles();
    }
  }, [activeFolder?.id]);

  // Update bucket change handler
  const handleBucketChange = (bucketId) => {
    console.log('Bucket changed to:', bucketId);
    setActiveBucket(bucketId);
    setCurrentBucket(bucketId);
    setCurrentPath('/');
    setSelectedFiles([]);
    setHasInitialLoadCompleted(false); // Reset to trigger new load
  };

  // Subscribe to real-time changes
  useEffect(() => {
    if (!user) return;

    const channel = uploadHistoryService?.subscribeToChanges(user?.id, (payload) => {
      if (payload?.eventType === 'INSERT') {
        // Add new upload to files list
        const newFile = {
          id: payload?.new?.id,
          name: payload?.new?.fileName,
          type: payload?.new?.fileType,
          size: payload?.new?.fileSize,
          lastModified: new Date(payload.new.createdAt),
          path: payload?.new?.filePath,
          isFolder: false,
          uploadStatus: payload?.new?.uploadStatus
        };
        setFiles(prev => [newFile, ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        // Update existing file in list
        setFiles(prev => prev?.map(file => 
          file?.id === payload?.new?.id ? {
            ...file,
            uploadStatus: payload?.new?.uploadStatus,
            lastModified: new Date(payload.new.createdAt)
          } : file
        ));
      } else if (payload?.eventType === 'DELETE') {
        // Remove deleted file from list
        setFiles(prev => prev?.filter(file => file?.id !== payload?.old?.id));
      }
    });

    return () => {
      uploadHistoryService?.unsubscribeFromChanges(channel);
    };
  }, [user]);

  const filteredFiles = files?.filter((file) =>
    file?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  // Add sorting functionality
  const sortedFiles = React.useMemo(() => {
    if (!filteredFiles) return [];
    
    const sorted = [...filteredFiles]?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      // Handle special cases
      if (sortConfig?.key === 'name') {
        // Folders first, then alphabetical
        if (a?.isFolder && !b?.isFolder) return -1;
        if (!a?.isFolder && b?.isFolder) return 1;
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      } else if (sortConfig?.key === 'size') {
        // Handle folders (no size)
        if (a?.isFolder && !b?.isFolder) return sortConfig?.direction === 'asc' ? -1 : 1;
        if (!a?.isFolder && b?.isFolder) return sortConfig?.direction === 'asc' ? 1 : -1;
        aValue = aValue || 0;
        bValue = bValue || 0;
      } else if (sortConfig?.key === 'lastModified') {
        aValue = new Date(aValue)?.getTime();
        bValue = new Date(bValue)?.getTime();
      } else if (sortConfig?.key === 'type') {
        aValue = aValue || '';
        bValue = bValue || '';
      }

      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [filteredFiles, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction: prevConfig?.key === key && prevConfig?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ChevronsUpDown" size={14} className="opacity-50" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ChevronUp" size={14} />
      : <Icon name="ChevronDown" size={14} />;
  };

  const getFileTypeIcon = (file) => {
    if (file?.isFolder) return { icon: 'Folder', color: 'text-yellow-500' };
    
    const name = file?.name?.toLowerCase();
    const type = file?.type;

    // Images
    if (type?.startsWith('image/') || /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i?.test(name)) {
      return { icon: 'Image', color: 'text-purple-500' };
    }
    // PDFs
    if (type === 'application/pdf' || name?.endsWith('.pdf')) {
      return { icon: 'FileText', color: 'text-red-500' };
    }
    // Videos
    if (type?.startsWith('video/') || /\.(mp4|webm|ogg|mov|avi)$/i?.test(name)) {
      return { icon: 'Video', color: 'text-blue-500' };
    }
    // Audio
    if (type?.startsWith('audio/') || /\.(mp3|wav|ogg|m4a|flac)$/i?.test(name)) {
      return { icon: 'Music', color: 'text-green-500' };
    }
    // Documents
    if (/\.(doc|docx|txt|rtf|odt)$/i?.test(name)) {
      return { icon: 'FileText', color: 'text-blue-600' };
    }
    // Spreadsheets
    if (/\.(xls|xlsx|csv|ods)$/i?.test(name)) {
      return { icon: 'Table', color: 'text-emerald-500' };
    }
    // Presentations
    if (/\.(ppt|pptx|odp)$/i?.test(name)) {
      return { icon: 'Presentation', color: 'text-orange-500' };
    }
    // Archives
    if (/\.(zip|rar|7z|tar|gz)$/i?.test(name)) {
      return { icon: 'Archive', color: 'text-gray-500' };
    }
    // Code files
    if (/\.(js|jsx|ts|tsx|py|java|cpp|c|cs|php|rb|go|rs)$/i?.test(name)) {
      return { icon: 'Code', color: 'text-cyan-500' };
    }

    return { icon: 'File', color: 'text-muted-foreground' };
  };

  const handleFileSelect = (fileId) => {
    setSelectedFiles((prev) =>
      prev?.includes(fileId) ? prev?.filter((id) => id !== fileId) : [...prev, fileId]
    );
  };

  const handleFilePreview = async (file) => {
    if (file?.isFolder) {
      setCurrentPath(file?.path + '/' + file?.name);
    } else {
      try {
        // Generate signed URL for preview
        const bucketConfig = bucketConfigs?.find(c => c?.id === activeBucket);
        if (bucketConfig) {
          await s3UploadService?.initializeClient(bucketConfig);
          const signedUrl = await s3UploadService?.getSignedDownloadUrl(file?.path, 3600);
          
          setPreviewFile({
            ...file,
            signedUrl: signedUrl // Add signed URL for preview
          });
          setIsPreviewModalOpen(true);
        }
      } catch (err) {
        console.error('[FileBrowser] Failed to generate preview URL:', err);
        showToast('Failed to generate preview', 'error');
      }
    }
  };

  const handleContextMenu = (e, file) => {
    e?.preventDefault();
    setContextMenu({
      isOpen: true,
      position: { x: e?.clientX, y: e?.clientY },
      file
    });
  };

  const handleBulkDownload = async () => {
    try {
      const bucketConfig = bucketConfigs?.find(c => c?.id === activeBucket);
      if (!bucketConfig) {
        throw new Error('Bucket configuration not found');
      }

      showToast('Starting download...', 'info');

      // Initialize S3 client
      await s3UploadService?.initializeClient(bucketConfig);

      // Download selected files
      const filesToDownload = files?.filter((file) => selectedFiles?.includes(file?.id));
      
      for (const file of filesToDownload) {
        try {
          await s3UploadService?.downloadFile(file?.path, file?.name);
        } catch (err) {
          console.error(`Failed to download ${file?.name}:`, err);
          showToast(`Failed to download ${file?.name}`, 'error');
        }
      }

      showToast(`Downloaded ${filesToDownload?.length} file(s)`, 'success');
      setSelectedFiles([]);

    } catch (err) {
      console.error('Bulk download error:', err);
      showToast('Failed to download files', 'error');
    }
  };

  const handleBulkDelete = () => {
    const filesToDelete = files?.filter((file) => selectedFiles?.includes(file?.id));
    setDeleteDialog({ isOpen: true, files: filesToDelete });
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      
      // Get the active bucket configuration
      const bucketConfig = bucketConfigs?.find(c => c?.id === activeBucket);
      if (!bucketConfig) {
        throw new Error('Bucket configuration not found');
      }

      console.log('[FileBrowser] Deleting files from S3...');

      // Initialize S3 client
      await s3UploadService?.initializeClient(bucketConfig);

      // Delete files from S3
      const keys = deleteDialog?.files?.map(file => file?.path);
      const result = await s3UploadService?.deleteMultipleFiles(keys, (progress) => {
        console.log('[FileBrowser] Delete progress:', progress);
      });

      console.log('[FileBrowser] Delete result:', result);

      if (result?.failed?.length > 0) {
        setError(`Failed to delete ${result?.failed?.length} file(s)`);
      }

      // Reload files from S3
      const listResult = await s3UploadService?.listFiles({
        prefix: currentPath === '/' ? '' : currentPath,
        maxKeys: 1000
      });

      setFiles(listResult?.files || []);
      setSelectedFiles([]);
      setDeleteDialog({ isOpen: false, files: [] });

    } catch (err) {
      console.error('[FileBrowser] Error deleting files:', err);
      setError(err?.message || 'Failed to delete files');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (uploadedFiles) => {
    if (!uploadedFiles || uploadedFiles?.length === 0) {
      showToast('No files selected', 'warning');
      return;
    }

    if (!activeBucket) {
      showToast('No S3 bucket configured', 'error');
      setError('No S3 bucket configured. Please configure a bucket in Settings.');
      return;
    }

    setIsActuallyUploading(true);
    setError('');

    try {
      // Get the active bucket configuration
      const bucketConfig = bucketConfigs?.find(c => c?.id === activeBucket);
      if (!bucketConfig) {
        throw new Error('Bucket configuration not found');
      }

      console.log('📤 Starting upload with active folder scoping...', {
        fileCount: uploadedFiles?.length,
        bucket: bucketConfig?.bucketName,
        activeFolder: activeFolder?.folder_path
      });

      // Initialize S3 client
      await s3UploadService?.initializeClient(bucketConfig);

      // CRITICAL FIX: Set active folder in service before uploading
      if (activeFolder?.folder_path) {
        s3UploadService?.setActiveFolder(activeFolder?.folder_path);
        console.log('🎯 Active folder set in S3 service:', activeFolder?.folder_path);
      }

      let successCount = 0;
      let failureCount = 0;

      // Upload files sequentially
      for (const file of uploadedFiles) {
        try {
          console.log(`📦 Uploading file: ${file?.name} to ${activeFolder?.folder_path || 'bucket root'}`);
          
          const uploadId = `upload-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`;
          
          // CRITICAL FIX: Don't pass destinationFolder - service uses active folder internally
          const result = await s3UploadService?.uploadFileWithRetry(file, {
            preserveMetadata: true,
            uploadId: uploadId,
            timeout: 180000, // 3 minutes
            maxRetries: 3,
            retryDelay: 2000,
            onProgress: (progressData) => {
              console.log(`[FileBrowser] Upload progress for ${file?.name}:`, progressData?.progress + '%');
            }
          });

          console.log(`✅ Upload successful to ${activeFolder?.folder_path || 'bucket root'}:`, result);
          successCount++;

        } catch (uploadError) {
          console.error(`❌ Upload failed for ${file?.name}:`, uploadError);
          failureCount++;
          
          // Check for CORS errors
          if (uploadError?.message === 'CORS_UPLOAD_ERROR' || uploadError?.troubleshooting?.type === 'CORS_UPLOAD') {
            setShowCORSGuide(true);
            setError('CORS configuration required - your S3 bucket needs CORS settings to allow browser uploads');
            showToast('Upload blocked by CORS policy', 'error', 8000);
            break; // Stop uploading remaining files
          }
        }
      }

      // Reload files from S3 after upload - scoped to active folder
      if (successCount > 0) {
        await loadFiles();
      }

      // Show completion notifications
      if (successCount > 0 && failureCount === 0) {
        showToast(`🎉 Successfully uploaded ${successCount} file${successCount > 1 ? 's' : ''} to ${activeFolder?.folder_path || 'bucket'}!`, 'success');
      } else if (successCount > 0 && failureCount > 0) {
        showToast(`Uploaded ${successCount} file${successCount > 1 ? 's' : ''}, ${failureCount} failed`, 'warning', 6000);
      } else if (failureCount > 0) {
        showToast(`Failed to upload ${failureCount} file${failureCount > 1 ? 's' : ''}`, 'error');
      }

    } catch (err) {
      console.error('[FileBrowser] Upload error:', err);
      setError(err?.message || 'Failed to upload files');
      showToast('Upload failed', 'error');
    } finally {
      setIsActuallyUploading(false);
    }
  };

  const handleFileDownload = async (file) => {
    try {
      const bucketConfig = bucketConfigs?.find(c => c?.id === activeBucket);
      if (!bucketConfig) {
        throw new Error('Bucket configuration not found');
      }

      console.log('[FileBrowser] Downloading file:', file?.name);
      showToast('Starting download...', 'info');

      // Initialize S3 client
      await s3UploadService?.initializeClient(bucketConfig);

      // Download file
      await s3UploadService?.downloadFile(file?.path, file?.name);

      showToast(`Downloaded ${file?.name}`, 'success');

    } catch (err) {
      console.error('[FileBrowser] Download error:', err);
      
      if (err?.message === 'CORS_DOWNLOAD_ERROR' || err?.troubleshooting?.type === 'CORS_DOWNLOAD') {
        setShowCORSGuide(true);
        setError('CORS configuration required - your S3 bucket needs CORS settings to allow browser downloads');
        showToast('Download blocked by CORS policy', 'error', 8000);
      } else {
        showToast(`Failed to download ${file?.name}`, 'error');
      }
    }
  };

  const handleCopyUrl = async (file) => {
    try {
      const bucketConfig = bucketConfigs?.find(c => c?.id === activeBucket);
      if (!bucketConfig) {
        throw new Error('Bucket configuration not found');
      }

      console.log('[FileBrowser] Copying URL for file:', file?.name);

      // Initialize S3 client
      await s3UploadService?.initializeClient(bucketConfig);

      // Copy URL to clipboard (use signed URL for private buckets)
      await s3UploadService?.copyFileUrl(file?.path, true, 3600); // 1 hour expiration

      showToast('URL copied to clipboard', 'success');

    } catch (err) {
      console.error('[FileBrowser] Copy URL error:', err);
      showToast('Failed to copy URL', 'error');
    }
  };

  const handleShareFile = async (file) => {
    try {
      const bucketConfig = bucketConfigs?.find(c => c?.id === activeBucket);
      if (!bucketConfig) {
        throw new Error('Bucket configuration not found');
      }

      console.log('[FileBrowser] Generating shareable link for file:', file?.name);
      showToast('Generating shareable link...', 'info');

      // Initialize S3 client
      await s3UploadService?.initializeClient(bucketConfig);

      // Generate shareable link (24 hours expiration)
      const result = await s3UploadService?.getShareableLink(file?.path, 86400);

      // Copy to clipboard
      await navigator.clipboard?.writeText(result?.shareUrl);

      showToast('Shareable link copied to clipboard (valid for 24 hours)', 'success', 5000);

    } catch (err) {
      console.error('[FileBrowser] Share error:', err);
      showToast('Failed to generate shareable link', 'error');
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e?.key === 'Escape') {
        setSelectedFiles([]);
      }
      if ((e?.ctrlKey || e?.metaKey) && e?.key === 'a') {
        e?.preventDefault();
        setSelectedFiles(files?.map((f) => f?.id));
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  function formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      {/* Mobile backdrop overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <div className="main-content">
        <div className="flex relative">
          <aside
            className={`fixed lg:sticky top-16 left-0 h-[calc(100vh-4rem)] bg-card border-r border-border transition-transform duration-300 ease-in-out z-30 w-80 overflow-y-auto scrollbar-thin ${
              isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
            }`}>
            <div className="p-6 space-y-6">
              {/* Close button for mobile */}
              <div className="lg:hidden flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-foreground">File Browser</h2>
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="w-8 h-8 rounded-md bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
                  aria-label="Close sidebar"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              <div>
                <h2 className="text-sm font-semibold text-foreground mb-4">Bucket Configuration</h2>
                <BucketSelector 
                  activeBucket={activeBucket} 
                  onBucketChange={handleBucketChange}
                  bucketConfigs={bucketConfigs}
                />
              </div>

              <StorageQuota used={calculateTotalStorageUsed()} total={100 * 1024 * 1024 * 1024} />

              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Info" size={20} className="text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Quick Actions</h3>
                </div>
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Upload"
                    iconPosition="left"
                    onClick={() => setIsUploadModalOpen(true)}>
                    Upload Files
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Settings"
                    iconPosition="left"
                    onClick={() => navigate('/settings')}>
                    Bucket Settings
                  </Button>
                </div>
              </div>

              {/* ENHANCED: Active folder indicator with sync status */}
              {activeFolder && (
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Folder" size={20} className="text-primary" />
                    <h3 className="text-sm font-semibold text-foreground">Active Folder</h3>
                    {folderSyncStatus?.status === 'verified' && (
                      <Icon name="CheckCircle" size={16} className="text-green-500 ml-auto" title="Sync verified" />
                    )}
                    {folderSyncStatus?.status === 'verifying' && (
                      <div className="ml-auto animate-spin">
                        <Icon name="Loader" size={16} className="text-primary" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground break-all">
                    {activeFolder?.folder_path}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    All files shown and uploaded to this location
                  </p>
                  {folderSyncStatus?.status === 'verified' && (
                    <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                      <Icon name="CheckCircle" size={12} />
                      Sync verified - ready for operations
                    </p>
                  )}
                  {folderSyncStatus?.status === 'verifying' && (
                    <p className="text-xs text-primary mt-2 flex items-center gap-1">
                      <Icon name="Loader" size={12} className="animate-spin" />
                      Verifying folder sync...
                    </p>
                  )}
                </div>
              )}

              <div className="bg-muted rounded-lg p-4">
                <h3 className="text-xs font-semibold text-foreground mb-2">Keyboard Shortcuts</h3>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <div className="flex justify-between">
                    <span>Select All</span>
                    <kbd className="px-2 py-0.5 bg-background rounded font-mono">Ctrl+A</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>Clear Selection</span>
                    <kbd className="px-2 py-0.5 bg-background rounded font-mono">Esc</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span>Upload</span>
                    <kbd className="px-2 py-0.5 bg-background rounded font-mono">Ctrl+U</kbd>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          <main className="flex-1 w-full lg:w-auto min-w-0">
            <div className="sticky top-16 bg-background border-b border-border z-20">
              <div className="px-4 lg:px-6 py-4">
                <div className="flex items-center gap-3 mb-4">
                  <button
                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                    className="lg:hidden w-10 h-10 rounded-md bg-card border border-border flex items-center justify-center hover:bg-muted transition-colors duration-150 flex-shrink-0"
                    aria-label={isSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
                  >
                    <Icon name="Menu" size={20} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <BreadcrumbNav 
                      path={currentPath} 
                      onNavigate={setCurrentPath}
                      activeFolder={activeFolder}
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <SearchBar value={searchQuery} onChange={setSearchQuery} />
                  </div>
                  
                  {/* View Mode Toggle */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`px-3 py-2 rounded-md border transition-colors ${
                        viewMode === 'grid' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border hover:bg-muted'
                      }`}
                      aria-label="Grid view"
                    >
                      <Icon name="Grid3x3" size={20} />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`px-3 py-2 rounded-md border transition-colors ${
                        viewMode === 'list' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border hover:bg-muted'
                      }`}
                      aria-label="List view"
                    >
                      <Icon name="List" size={20} />
                    </button>
                  </div>

                  <Button
                    iconName="Upload"
                    iconPosition="left"
                    onClick={() => setIsUploadModalOpen(true)}
                    className="w-full sm:w-auto whitespace-nowrap"
                  >
                    Upload
                  </Button>
                </div>
              </div>
            </div>

            <div className="px-4 lg:px-6 py-6">
              {/* SIMPLIFIED LOADING DISPLAY LOGIC */}
              {(isFolderLoading && !hasInitialLoadCompleted) ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                  <p className="ml-4 text-muted-foreground">
                    Loading folder configuration...
                  </p>
                </div>
              ) : (loading || isLoading) && !hasInitialLoadCompleted ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                  <p className="ml-4 text-muted-foreground">Loading files from S3...</p>
                </div>
              ) : showCORSGuide ? (
                <CORSTroubleshootingGuide
                  bucketName={bucketConfigs?.find(c => c?.id === activeBucket)?.bucketName}
                  region={bucketConfigs?.find(c => c?.id === activeBucket)?.bucketRegion}
                  onClose={() => setShowCORSGuide(false)}
                />
              ) : error && !showCORSGuide ? (
                <ErrorStateDisplay
                  error={error}
                  errorType={errorType}
                  onRetry={loadFiles}
                  onOpenSettings={() => navigate('/settings')}
                  onShowCORSGuide={() => setShowCORSGuide(true)}
                  bucketName={bucketConfigs?.find(c => c?.id === activeBucket)?.bucketName}
                  activeFolder={activeFolder?.folder_path}
                />
              ) : !activeBucket ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Icon name="Database" size={48} className="text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">No Bucket Selected</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Please configure an S3 bucket in settings to view your files
                  </p>
                  <Button
                    iconName="Settings"
                    iconPosition="left"
                    onClick={() => navigate('/settings')}
                  >
                    Go to Settings
                  </Button>
                </div>
              ) : filteredFiles?.length === 0 && !error ? (
                <EmptyState onUpload={() => setIsUploadModalOpen(true)} />
              ) : viewMode === 'grid' ? (
                <div className="file-grid">
                  {sortedFiles?.map((file) => (
                    <FileCard
                      key={file?.id}
                      file={file}
                      isSelected={selectedFiles?.includes(file?.id)}
                      onSelect={handleFileSelect}
                      onPreview={handleFilePreview}
                      onContextMenu={handleContextMenu}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-card border border-border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted border-b border-border">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-foreground w-12">
                          <input
                            type="checkbox"
                            checked={selectedFiles?.length === sortedFiles?.length && sortedFiles?.length > 0}
                            onChange={(e) => {
                              if (e?.target?.checked) {
                                setSelectedFiles(sortedFiles?.map(f => f?.id));
                              } else {
                                setSelectedFiles([]);
                              }
                            }}
                            className="rounded border-border"
                          />
                        </th>
                        <th 
                          className="text-left py-3 px-4 text-sm font-semibold text-foreground cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('name')}
                        >
                          <div className="flex items-center gap-2">
                            <span>Name</span>
                            {getSortIcon('name')}
                          </div>
                        </th>
                        <th 
                          className="text-left py-3 px-4 text-sm font-semibold text-foreground cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('type')}
                        >
                          <div className="flex items-center gap-2">
                            <span>Type</span>
                            {getSortIcon('type')}
                          </div>
                        </th>
                        <th 
                          className="text-left py-3 px-4 text-sm font-semibold text-foreground cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('size')}
                        >
                          <div className="flex items-center gap-2">
                            <span>Size</span>
                            {getSortIcon('size')}
                          </div>
                        </th>
                        <th 
                          className="text-left py-3 px-4 text-sm font-semibold text-foreground cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('lastModified')}
                        >
                          <div className="flex items-center gap-2">
                            <span>Modified</span>
                            {getSortIcon('lastModified')}
                          </div>
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedFiles?.map((file) => {
                        const fileTypeInfo = getFileTypeIcon(file);
                        return (
                          <tr
                            key={file?.id}
                            className={`border-b border-border hover:bg-muted/50 transition-colors ${
                              selectedFiles?.includes(file?.id) ? 'bg-primary/5' : ''
                            }`}
                            onContextMenu={(e) => handleContextMenu(e, file)}
                          >
                            <td className="py-3 px-4">
                              <input
                                type="checkbox"
                                checked={selectedFiles?.includes(file?.id)}
                                onChange={() => handleFileSelect(file?.id)}
                                className="rounded border-border"
                              />
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3">
                                <Icon
                                  name={fileTypeInfo?.icon}
                                  size={20}
                                  className={`${fileTypeInfo?.color} flex-shrink-0`}
                                />
                                <button
                                  onClick={() => handleFilePreview(file)}
                                  className="text-sm font-medium text-foreground hover:text-primary truncate text-left"
                                >
                                  {file?.name}
                                </button>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <Icon
                                  name={fileTypeInfo?.icon}
                                  size={16}
                                  className={fileTypeInfo?.color}
                                />
                                <span className="text-sm text-muted-foreground capitalize">
                                  {file?.type?.split('/')?.[1] || file?.name?.split('.')?.pop() || 'file'}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm text-muted-foreground font-mono">
                              {file?.isFolder ? '-' : formatFileSize(file?.size || 0)}
                            </td>
                            <td className="py-3 px-4 text-sm text-muted-foreground font-mono">
                              {new Date(file?.lastModified || new Date())?.toLocaleDateString()}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleFileDownload(file)}
                                  className="p-1 hover:bg-muted rounded"
                                  title="Download"
                                >
                                  <Icon name="Download" size={16} />
                                </button>
                                <button
                                  onClick={(e) => handleContextMenu(e, file)}
                                  className="p-1 hover:bg-muted rounded"
                                  title="More options"
                                >
                                  <Icon name="MoreVertical" size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
      <BulkActions
        selectedCount={selectedFiles?.length}
        onDownload={handleBulkDownload}
        onDelete={handleBulkDelete}
        onClear={() => setSelectedFiles([])} />
      <UploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUpload}
        isActuallyUploading={isActuallyUploading} />
      <FilePreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        file={previewFile}
        onDownload={handleFileDownload}
        bucketConfig={bucketConfigs?.find(c => c?.id === activeBucket)}
      />
      <ContextMenu
        isOpen={contextMenu?.isOpen}
        position={contextMenu?.position}
        file={contextMenu?.file}
        onClose={() => setContextMenu({ ...contextMenu, isOpen: false })}
        onPreview={() => {
          if (contextMenu?.file) {
            handleFilePreview(contextMenu?.file);
          }
        }}
        onDownload={() => {
          if (contextMenu?.file) {
            handleFileDownload(contextMenu?.file);
          }
        }}
        onCopyUrl={() => {
          if (contextMenu?.file) {
            handleCopyUrl(contextMenu?.file);
          }
        }}
        onShare={() => {
          if (contextMenu?.file) {
            handleShareFile(contextMenu?.file);
          }
        }}
        onDelete={() => {
          if (contextMenu?.file) {
            setDeleteDialog({ isOpen: true, files: [contextMenu?.file] });
          }
        }} />
      <DeleteConfirmDialog
        isOpen={deleteDialog?.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, files: [] })}
        files={deleteDialog?.files}
        onConfirm={handleDeleteConfirm} />
    </div>
  );
};

export default FileBrowser;